// docs/35 WL5 — where a creature is and how it looks at a moment. A `wildlife` row is a timed
// plan (fk_wild::Leg: be at (x, y), z high, by `at + ms`, moving like `gait`); this file turns it
// and a server time into a pose: the position along the leg with the gait's easing, the
// species' flourish (a butterfly's wobble, a bat's jitter, a spider's sway, a hop's arc) and the
// sprite frame. Everything is a function of the row and the time — no state, no randomness but
// the row's seed — so every client in the party draws the same creature in the same place.

/** Species codes (`fk_wild::Species`). */
export const SPECIES = ['butterfly', 'songbird', 'crow', 'swallow', 'hawk', 'bat', 'moth', 'firefly', 'mouse', 'rat', 'spider', 'scorpion', 'lizard', 'gnats', 'dragonfly', 'frog'] as const;
export type Species = (typeof SPECIES)[number];

/** Gait codes (`fk_wild::Gait`). */
export const GAIT = { rest: 0, walk: 1, dash: 2, flutter: 3, fly: 4, glide: 5, hop: 6, flit: 7, hover: 8, drop: 9, perch: 10, emerge: 11, vanish: 12, peck: 13, swarm: 14 } as const;
const G = GAIT;

/** Pips per square (`fk_wild::PIP`). */
export const PIP = 16;
/** How high a spider's thread is tied (`fk_wild::brain::SPIDER_TOP`). */
export const SPIDER_TOP = 26;

export interface WildLeg { at: number; ms: number; x: number; y: number; z: number; gait: number }
export interface WildRow { id: number; species: number; seed: number; legs: WildLeg[]; attend?: number; leaving: boolean }

export interface Pose {
  species: Species;
  /** Pips: ground point and height. */
  x: number; y: number; z: number;
  /** 1 faces east (the art), -1 west. */
  face: 1 | -1;
  /** Frame name inside the species' sheet (game/wildlife/sprites.ts). */
  frame: string;
  alpha: number;
  /** A firefly's light, 0..1. */
  glow: number;
  gait: number;
  /** In the air (casts a shadow, drawn with its height). */
  airborne: boolean;
}

/** A row from the SDK (bigint ids and times) → numbers. */
export function rowOf(r: { id: bigint | number; species: number; seed: number; legs: ReadonlyArray<{ at: bigint | number; ms: number; x: number; y: number; z: number; gait: number }>; attend?: number | undefined; leaving: boolean }): WildRow {
  const out: WildRow = { id: Number(r.id), species: r.species, seed: r.seed >>> 0, legs: r.legs.map((l) => ({ at: Number(l.at), ms: l.ms, x: l.x, y: l.y, z: l.z, gait: l.gait })), leaving: r.leaving };
  if (r.attend !== undefined && r.attend !== null) out.attend = r.attend;
  return out;
}

/** A seeded 0..1 from the row's seed and a salt (the flourish's phases and rates). */
export function seedUnit(seed: number, salt: number): number {
  let h = (seed ^ Math.imul(salt + 0x9e3779b9, 0x85ebca6b)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

const TAU = Math.PI * 2;
const smooth = (k: number): number => k * k * (3 - 2 * k);
const easeOut = (k: number): number => 1 - (1 - k) * (1 - k);
const clamp01 = (k: number): number => (k < 0 ? 0 : k > 1 ? 1 : k);
const lerp = (a: number, b: number, k: number): number => a + (b - a) * k;
/** Uniform Catmull-Rom through p1 → p2 (curving flight across leg ends). */
const cr = (p0: number, p1: number, p2: number, p3: number, k: number): number =>
  0.5 * (2 * p1 + (-p0 + p2) * k + (2 * p0 - 5 * p1 + 4 * p2 - p3) * k * k + (-p0 + 3 * p1 - 3 * p2 + p3) * k * k * k);

/** The leg running at `t` (the last one that has begun), or -1 before the first. */
export function legAt(legs: readonly WildLeg[], t: number): number {
  let lo = 0, hi = legs.length - 1, found = -1;
  while (lo <= hi) { const m = (lo + hi) >> 1; if (legs[m]!.at <= t) { found = m; lo = m + 1; } else hi = m - 1; }
  return found;
}

/** Frames per second of a flap, a scurry, a wingbeat, by species (scaled a little by the seed). */
function rate(sp: Species, seed: number): number {
  const base: Record<Species, number> = { butterfly: 11, songbird: 10, crow: 6, swallow: 7, hawk: 1.5, bat: 12, moth: 16, firefly: 0, mouse: 12, rat: 9, spider: 0, scorpion: 5, lizard: 14, gnats: 0, dragonfly: 22, frog: 0 };
  return base[sp] * (0.85 + 0.3 * seedUnit(seed, 1));
}

/** Pose of `row` at server ms `t`; `undefined` while it has not arrived or once it has gone. `still` (reduce motion) drops the jitter and wobble. */
export function poseAt(row: WildRow, t: number, still = false): Pose | undefined {
  const sp = SPECIES[row.species];
  const legs = row.legs;
  if (!sp || legs.length === 0) return undefined;
  const i = legAt(legs, t);
  if (i < 0) return undefined;
  const leg = legs[i]!;
  const from = i > 0 ? legs[i - 1]! : leg;
  const ended = t >= leg.at + leg.ms;
  if (ended && i === legs.length - 1 && (leg.gait === G.vanish)) return undefined;
  const k = leg.ms > 0 ? clamp01((t - leg.at) / leg.ms) : 1;
  const s = row.seed, sec = t / 1000;
  const ph = (salt: number) => seedUnit(s, salt) * TAU;
  const amp = still ? 0 : 1;
  let x: number, y: number, z: number;
  const g = leg.gait;
  switch (g) {
    case G.dash: { const e = easeOut(k); x = lerp(from.x, leg.x, e); y = lerp(from.y, leg.y, e); z = lerp(from.z, leg.z, e); break; }
    case G.walk: x = lerp(from.x, leg.x, k); y = lerp(from.y, leg.y, k); z = lerp(from.z, leg.z, k); break;
    case G.hop: {
      x = lerp(from.x, leg.x, k); y = lerp(from.y, leg.y, k);
      const high = sp === 'frog' ? 5 : sp === 'songbird' ? 2.5 : 2;
      z = lerp(from.z, leg.z, k) + Math.sin(Math.PI * k) * high;
      break;
    }
    case G.fly: case G.glide: {
      // a curve through the leg ends: the leg before and the one after bend it
      const p0 = i > 1 ? legs[i - 2]! : from, p3 = i + 1 < legs.length ? legs[i + 1]! : leg;
      const curve = (p0.gait === G.fly || p0.gait === G.glide) && (p3.gait === G.fly || p3.gait === G.glide);
      if (curve) { x = cr(p0.x, from.x, leg.x, p3.x, k); y = cr(p0.y, from.y, leg.y, p3.y, k); }
      else { const e = smooth(k); x = lerp(from.x, leg.x, e); y = lerp(from.y, leg.y, e); }
      z = lerp(from.z, leg.z, smooth(k));
      break;
    }
    case G.flutter: {
      const e = smooth(k);
      x = lerp(from.x, leg.x, e); y = lerp(from.y, leg.y, e); z = lerp(from.z, leg.z, e);
      // the drunken line: a wobble across the way it goes (zero at the leg's ends, so legs join)
      const dx = leg.x - from.x, dy = leg.y - from.y, d = Math.hypot(dx, dy) || 1;
      const w = Math.sin(Math.PI * k) * 2.4 * amp * Math.sin(sec * TAU * (0.9 + 0.5 * seedUnit(s, 2)) + ph(3));
      x += (-dy / d) * w; y += (dx / d) * w * 0.6;
      // and a bob, every wingbeat a little lift
      z += Math.min(1, z / 3) * amp * (1.2 * Math.sin(sec * TAU * 1.7 + ph(4)) + 0.5 * Math.sin(sec * TAU * 5.3 + ph(5)));
      break;
    }
    case G.flit: {
      x = lerp(from.x, leg.x, k); y = lerp(from.y, leg.y, k); z = lerp(from.z, leg.z, k);
      const j = Math.sin(Math.PI * k) * amp;
      x += j * 1.6 * Math.sin(sec * 23 + ph(6)); y += j * 1.1 * Math.sin(sec * 19 + ph(7)); z += j * 1.4 * Math.sin(sec * 17 + ph(8));
      break;
    }
    case G.hover: case G.swarm: {
      const e = smooth(k);
      x = lerp(from.x, leg.x, e); y = lerp(from.y, leg.y, e); z = lerp(from.z, leg.z, e);
      // a figure of eight in the air
      const r = g === G.hover ? (sp === 'firefly' ? 1.6 : 0.8) : 1.2;
      x += amp * r * Math.sin(sec * TAU * 0.45 + ph(9)); z += amp * r * 0.7 * Math.sin(sec * TAU * 0.9 + ph(10));
      break;
    }
    case G.drop: {
      x = leg.x; y = leg.y; z = lerp(from.z, leg.z, smooth(k));
      break;
    }
    default:
      x = leg.x; y = leg.y; z = leg.z;
  }
  if (sp === 'spider') x += amp * 0.7 * Math.sin(sec * TAU * 0.4 + ph(11)) * Math.min(1, (SPIDER_TOP - z) / 10);
  if (g === G.perch || (sp === 'butterfly' && g === G.rest)) z += amp * 0.25 * Math.sin(sec * TAU * 0.6 + ph(12));
  // facing: the way it is going, else the way it last went
  let face: 1 | -1 = seedUnit(s, 13) < 0.5 ? 1 : -1;
  for (let j = i; j >= Math.max(1, i - 8); j--) {
    const a = legs[j - 1]!, b = legs[j]!;
    if (Math.abs(b.x - a.x) > 0.5) { face = b.x > a.x ? 1 : -1; break; }
  }
  const alpha = g === G.emerge ? k : g === G.vanish ? 1 - k : 1;
  const airborne = z > 2 || g === G.flutter || g === G.fly || g === G.glide || g === G.flit || g === G.hover || g === G.swarm;
  return { species: sp, x, y, z, face, frame: frameOf(sp, g, t, s, airborne, k), alpha, glow: sp === 'firefly' ? glowOf(s, t) : 0, gait: g, airborne };
}

/** A firefly's light: a slow pulse a second or so long, every few seconds, on its own clock. */
export function glowOf(seed: number, t: number): number {
  const period = 2200 + 2600 * seedUnit(seed, 20);
  const on = 700 + 600 * seedUnit(seed, 21);
  const p = (t + seedUnit(seed, 22) * period) % period;
  if (p > on) return 0.12;
  return 0.12 + 0.88 * Math.sin((p / on) * Math.PI);
}

/** Which frame of the species' sheet (sprites.ts names) to draw. */
export function frameOf(sp: Species, gait: number, t: number, seed: number, airborne: boolean, k: number): string {
  const n = Math.floor((t / 1000) * rate(sp, seed) + seedUnit(seed, 14) * 4);
  const slow = (period: number, share: number, salt: number) => ((t + seedUnit(seed, salt) * period) % period) < period * share;
  switch (sp) {
    case 'butterfly':
      if (gait === G.rest || gait === G.perch) return slow(1700, 0.22, 15) ? 'open' : 'shut';
      return ['open', 'half', 'shut', 'half'][n % 4]!;
    case 'songbird': case 'crow':
      if (airborne) return n % 2 ? 'flyUp' : 'flyDown';
      if (gait === G.peck) return (n >> 1) % 2 ? 'peck' : 'stand';
      if (gait === G.walk) return n % 2 ? 'walk' : 'stand';
      return 'stand';
    case 'swallow':
      return gait === G.fly ? (n % 2 ? 'flap' : 'glide') : 'glide';
    case 'hawk':
      return slow(5200, 0.1, 16) ? 'flap' : 'glide';
    case 'bat': case 'moth': case 'dragonfly':
      return n % 2 ? 'a' : 'b';
    case 'mouse': case 'rat':
      if (gait === G.dash || gait === G.walk) return n % 2 ? 'run' : 'stand';
      return slow(1100, 0.3, 17) ? 'sniff' : 'stand';
    case 'scorpion':
      if (gait === G.walk || gait === G.dash) return n % 2 ? 'walk' : 'stand';
      return slow(3000, 0.25, 18) ? 'sting' : 'stand';
    case 'lizard':
      if (gait === G.dash) return n % 2 ? 'run' : 'stand';
      return slow(1500, 0.18, 19) ? 'bob' : 'stand';
    case 'frog':
      if (gait === G.hop) return k > 0.1 && k < 0.9 ? 'jump' : 'sit';
      return slow(900, 0.35, 23) ? 'puff' : 'sit';
    default:
      return 'a';
  }
}

/** A swarm's motes around its point (pips, relative), each on its own loop. */
export function motes(seed: number, t: number, n = 7, still = false): Array<{ dx: number; dy: number; dz: number }> {
  const sec = t / 1000, out = [];
  const a = still ? 0.4 : 1;
  for (let i = 0; i < n; i++) {
    const f1 = 1.3 + 2.1 * seedUnit(seed, 30 + i), f2 = 1.1 + 2.6 * seedUnit(seed, 50 + i), p = seedUnit(seed, 70 + i) * TAU;
    const r = 1.8 + 2.2 * seedUnit(seed, 90 + i);
    out.push({ dx: a * r * Math.sin(sec * f1 * TAU * 0.5 + p), dy: a * r * 0.5 * Math.cos(sec * f2 * TAU * 0.5 + p * 1.3), dz: a * 1.6 * Math.sin(sec * (f1 + f2) * 1.7 + p) });
  }
  return out;
}
